import React from 'react';
import { Target, Users, Zap, Award } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const { language } = useLanguage();

  const values = [
    {
      icon: <Target className="w-12 h-12 text-green-400" />,
      title: language === 'es' ? 'Misión' : 'Mission',
      description: language === 'es' 
        ? 'Revolucionar la forma en que las empresas interactúan con sus clientes mediante agentes de IA inteligentes y eficientes.'
        : 'Revolutionize the way businesses interact with their customers through intelligent and efficient AI agents.'
    },
    {
      icon: <Users className="w-12 h-12 text-green-400" />,
      title: language === 'es' ? 'Equipo' : 'Team',
      description: language === 'es'
        ? 'Un equipo dedicado de expertos en IA y tecnología comprometidos con la excelencia y la innovación.'
        : 'A dedicated team of AI and technology experts committed to excellence and innovation.'
    },
    {
      icon: <Zap className="w-12 h-12 text-green-400" />,
      title: language === 'es' ? 'Innovación' : 'Innovation',
      description: language === 'es'
        ? 'Constantemente evolucionamos y mejoramos nuestras soluciones para mantener a nuestros clientes a la vanguardia.'
        : 'We constantly evolve and improve our solutions to keep our customers at the forefront.'
    },
    {
      icon: <Award className="w-12 h-12 text-green-400" />,
      title: language === 'es' ? 'Excelencia' : 'Excellence',
      description: language === 'es'
        ? 'Compromiso con la calidad en cada interacción, cada proyecto y cada resultado.'
        : 'Commitment to quality in every interaction, every project and every result.'
    }
  ];

  return (
    <div className="bg-black text-white min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-green-900/20 via-black to-emerald-900/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {language === 'es' ? 'Sobre' : 'About'}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                GERVIS
              </span>
            </h1>
            <p className="text-xl text-gray-200">
              {language === 'es'
                ? 'Somos una agencia especializada en el desarrollo de agentes de IA de voz y texto para empresas que buscan transformar sus procesos de comunicación y ventas.'
                : 'We are an agency specialized in developing voice and text AI agents for businesses looking to transform their communication and sales processes.'}
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              {language === 'es' ? 'Nuestra Historia' : 'Our Story'}
            </h2>
            <div className="space-y-6 text-lg text-gray-200">
              <p>
                {language === 'es'
                  ? 'GERVIS nació de la necesidad de ayudar a las empresas a responder rápidamente a sus leads y mejorar sus tasas de conversión. Entendimos que en el mundo digital actual, la velocidad de respuesta es crucial para el éxito de cualquier negocio.'
                  : 'GERVIS was born from the need to help businesses respond quickly to their leads and improve their conversion rates. We understood that in today\'s digital world, response speed is crucial for the success of any business.'}
              </p>
              <p>
                {language === 'es'
                  ? 'Con años de experiencia en inteligencia artificial y automatización, desarrollamos una plataforma que permite a las empresas contactar a sus leads en menos de un minuto, mantener conversaciones inteligentes y recuperar oportunidades perdidas.'
                  : 'With years of experience in artificial intelligence and automation, we developed a platform that allows businesses to contact their leads in less than a minute, maintain intelligent conversations and recover lost opportunities.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            {language === 'es' ? 'Nuestros Valores' : 'Our Values'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <Card key={index} className="bg-gradient-to-br from-gray-900 to-black border-green-900/30 hover:border-green-600/50 transition-all duration-300">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-green-600/20 rounded-2xl flex items-center justify-center mb-6">
                    {value.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-white">{value.title}</h3>
                  <p className="text-gray-200 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;